import { Octokit } from '@octokit/rest';
import { GetResponseDataTypeFromEndpointMethod } from '@octokit/types';
import { getMyUser } from './users';
import { flattenObject } from './utils/flattenObject';

const ONE_HOUR = 60 * 60 * 1000; /* ms */

const octokit = new Octokit();

export type GetIssuesResponseType = GetResponseDataTypeFromEndpointMethod<
  typeof octokit.issues.listForRepo
>;

export const setItemInLocalStorage = (key: string, data: any) => {
  const versionedData = {
    timestamp: Date.now(),
    data,
  };
  localStorage.setItem(key, JSON.stringify(versionedData));
};

const ISSUES_KEY = 'ADAPTABLE';

export const getItemFromLocalStorage = (key: string): any => {
  try {
    const cachedData = JSON.parse(localStorage.getItem(key) as string);
    // return data only if it is not older than 1 hour
    if (cachedData?.timestamp && Date.now() - cachedData.timestamp < ONE_HOUR) {
      return cachedData.data;
    }
  } catch (e) {
    return null;
  }
};

export const fetchAllIssues = async (
  owner: string = 'facebook',
  repository: string = 'react',
  maxNumPages: number = 1
): Promise<any[]> => {
  const key = `${ISSUES_KEY}:${owner}:${repository}`;
  const data = getItemFromLocalStorage(key);

  if (data) {
    return data;
  }

  const iterator = octokit.paginate.iterator(octokit.rest.issues.listForRepo, {
    owner,
    repo: repository,
    per_page: 100,
    state: 'all',
  });

  const items = [];
  let pageNum = 1;
  for await (const { data: issues } of iterator) {
    pageNum += 1;
    for (const issue of issues) {
      const flatIssue = flattenObject(issue);

      // randomly set myself as the author
      if (Math.random() < 0.05) {
        const user = getMyUser();
        flatIssue['user:login'] = user.login;
        flatIssue['user:html_url'] = user.html_url;
        flatIssue['user:avatar_url'] = user.avatar_url;
      }

      // pick random assignee
      // const assignee = pickUser();
      // flatIssue['assignee:login'] = assignee.name;
      // flatIssue['assignee:html_url'] = assignee.img;

      // ensure assignee fields are always present (even if undefined) - these ensures a smooth change detection
      [
        'assignee:id',
        'assignee:html_url',
        'assignee:login',
        'assignee:avatar_url',
      ].forEach((field) => {
        if (!flatIssue.hasOwnProperty(field)) {
          flatIssue[field] = null;
        }
      });

      flatIssue['reactions'] =
        flatIssue['reactions:+1'] - flatIssue['reactions:-1'];

      items.push(flatIssue);
    }
    if (pageNum >= maxNumPages) {
      break;
    }
  }

  setItemInLocalStorage(key, items);

  return items;
};
